import { Box } from '@mui/material';
import { Img } from '@filejet/sdk/react';

/**
 * A product or reward image.
 *
 * FileJet stores by id: file_identifier on a product, and on a loyalty reward,
 * is the id the CDN serves. The SDK builds the URL and the srcset, which is
 * why this uses <Img> rather than composing a URL by hand - the storefront's
 * own mutation strings are fiddly enough that duplicating them here would drift.
 *
 * Renders nothing when there is no image, rather than a broken-image icon or a
 * grey box: most products have none, and a column of placeholders is noise.
 */
export function FileImage({ id, size = 40, alt = '' }: { id?: string | null; size?: number; alt?: string }) {
  if (id == null || id === '') return null;

  return (
    <Box sx={{ width: size, height: size, '& img': { objectFit: 'contain', borderRadius: 1 } }}>
      <Img src={id} width={size} height={size} fit="contain" alt={alt} />
    </Box>
  );
}

/**
 * A banner, shown wide enough to read.
 *
 * Offer banners are ~968px of artwork with text on them; squeezed into a
 * thumbnail there is no way to tell which promotion you are looking at.
 *
 * A plain <img> with a URL built by hand, rather than the SDK's <Img>: that
 * component wants both dimensions to reserve space, and a banner's height
 * depends on the artwork. Given only a width it laid out at zero height and
 * the picture spilled over the text beside it. Here the browser derives the
 * height from the image, which is what "show it at its own aspect ratio"
 * actually needs.
 */
export function FileBanner({ id, width = 640, alt = '' }: { id?: string | null; width?: number; alt?: string }) {
  if (id == null || id === '') return null;

  const domain = import.meta.env['VITE_FILEJET_DOMAIN'] ?? 'hz0ur4.5gcdn.net';

  return (
    <Box
      component="img"
      src={`https://${domain}/${id}/resize_${width}`}
      alt={alt}
      loading="lazy"
      sx={{
        display: 'block',
        width: '100%',
        maxWidth: width,
        height: 'auto',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider'
      }}
    />
  );
}
